/**
 * Timezone Context - Organization-aware timezone management
 * Provides timezone-aware date formatting functions throughout the application
 *
 * Key Features:
 * - Fetches timezone from organization settings
 * - Provides formatters that convert UTC dates to org timezone
 * - Falls back to UTC if timezone not available
 */

import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { format, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { parseISO, isValid, startOfDay, endOfDay, subDays } from 'date-fns';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_TIMEZONE = 'UTC';
const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';
const DEFAULT_TIME_FORMAT = 'hh:mm a';
const _DEFAULT_DATETIME_FORMAT = 'dd/MM/yyyy hh:mm a';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface TimezoneContextType {
  // Current timezone from organization
  timezone: string;
  dateFormat: string;
  timeFormat: string;

  // Format UTC date to organization timezone
  formatDate: (date: string | Date | null | undefined) => string;
  formatTime: (date: string | Date | null | undefined) => string;
  formatDateTime: (date: string | Date | null | undefined) => string;
  formatDateShort: (date: string | Date | null | undefined) => string;
  formatDateFull: (date: string | Date | null | undefined) => string;
  formatDateTimeDisplay: (date: string | Date | null | undefined) => string;
  formatRelativeTime: (date: string | Date | null | undefined) => string;

  // For date inputs - convert local input to UTC for API
  toUTC: (date: Date | string, isEndOfDay?: boolean) => Date;

  // Get current date/time in org timezone
  getCurrentDate: () => Date;
  getCurrentDateString: () => string;
  getDateNDaysAgo: (days: number) => string;

  // Get date range in UTC for API queries (from org timezone boundaries)
  getTodayRangeUTC: () => { start: Date; end: Date };
  getDateRangeUTC: (startDate: string | Date, endDate?: string | Date) => { start: Date; end: Date };

  // Check if date is today in org timezone
  isToday: (date: string | Date) => boolean;
  isPast: (date: string | Date) => boolean;
  isFuture: (date: string | Date) => boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse date string or Date object to valid Date
 */
const parseDate = (date: string | Date | null | undefined): Date | null => {
  if (!date) return null;

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? dateObj : null;
  } catch {
    return null;
  }
};

// ============================================================================
// TIMEZONE PROVIDER COMPONENT
// ============================================================================

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const { user } = useAuth();

  // Get timezone from organization settings or use default
  const timezone = useMemo(() => {
    return user?.organization?.timezone || DEFAULT_TIMEZONE;
  }, [user?.organization?.timezone]);

  const dateFormat = useMemo(() => {
    return user?.organization?.dateFormat || DEFAULT_DATE_FORMAT;
  }, [user?.organization?.dateFormat]);

  const timeFormat = useMemo(() => {
    return user?.organization?.timeFormat || DEFAULT_TIME_FORMAT;
  }, [user?.organization?.timeFormat]);

  /**
   * Format date (date only)
   */
  const formatDate = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '-';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'dd/MM/yyyy', { timeZone: timezone });
    } catch {
      return '-';
    }
  }, [timezone]);

  /**
   * Format time only (HH:MM AM/PM)
   */
  const formatTime = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '-';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'hh:mm a', { timeZone: timezone });
    } catch {
      return '-';
    }
  }, [timezone]);

  /**
   * Format date and time together
   */
  const formatDateTime = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '-';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'dd MMM yyyy, hh:mm a', { timeZone: timezone });
    } catch {
      return '-';
    }
  }, [timezone]);

  /**
   * Format date short (DD/MM/YYYY)
   */
  const formatDateShort = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'dd/MM/yyyy', { timeZone: timezone });
    } catch {
      return '';
    }
  }, [timezone]);

  /**
   * Format date full with weekday (Monday, 18 January 2026)
   */
  const formatDateFull = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '-';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'EEEE, d MMMM yyyy', { timeZone: timezone });
    } catch {
      return '-';
    }
  }, [timezone]);

  /**
   * Format date-time for display (18 Jan 2026, 10:30 AM)
   */
  const formatDateTimeDisplay = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '-';

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      return format(zonedDate, 'dd MMM yyyy, hh:mm a', { timeZone: timezone });
    } catch {
      return '-';
    }
  }, [timezone]);

  /**
   * Format relative time (e.g., "2 hours ago", "3 days ago")
   */
  const formatRelativeTime = useCallback((date: string | Date | null | undefined): string => {
    const parsed = parseDate(date);
    if (!parsed) return '';

    try {
      const now = new Date();
      const diff = now.getTime() - parsed.getTime();

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const months = Math.floor(days / 30);
      const years = Math.floor(days / 365);

      if (seconds < 60) return 'just now';
      if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
      if (weeks < 4) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
      if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
      return `${years} year${years === 1 ? '' : 's'} ago`;
    } catch {
      return '';
    }
  }, []);

  /**
   * Convert local date input to UTC for API submission
   * Takes a date selected in org timezone and converts to UTC
   */
  const toUTC = useCallback((date: Date | string, isEndOfDay = false): Date => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;

      // Create date in org timezone
      const zonedDate = isEndOfDay ? endOfDay(dateObj) : startOfDay(dateObj);

      // Convert to UTC
      return fromZonedTime(zonedDate, timezone);
    } catch {
      return new Date();
    }
  }, [timezone]);

  /**
   * Get current date in org timezone
   */
  const getCurrentDate = useCallback((): Date => {
    return toZonedTime(new Date(), timezone);
  }, [timezone]);

  /**
   * Get current date string in YYYY-MM-DD format in org timezone
   */
  const getCurrentDateString = useCallback((): string => {
    const zonedDate = toZonedTime(new Date(), timezone);
    return format(zonedDate, 'yyyy-MM-dd', { timeZone: timezone });
  }, [timezone]);

  /**
   * Get date N days ago in YYYY-MM-DD format in org timezone
   */
  const getDateNDaysAgo = useCallback((days: number): string => {
    const zonedNow = toZonedTime(new Date(), timezone);
    const nDaysAgo = subDays(zonedNow, days);
    return format(nDaysAgo, 'yyyy-MM-dd', { timeZone: timezone });
  }, [timezone]);

  /**
   * Get today's date range in UTC for API queries
   * Returns start and end of today in org timezone, converted to UTC
   */
  const getTodayRangeUTC = useCallback((): { start: Date; end: Date } => {
    const now = new Date();
    const zonedNow = toZonedTime(now, timezone);

    // Get start of day in org timezone
    const startOfToday = startOfDay(zonedNow);
    const endOfToday = endOfDay(zonedNow);

    // Convert back to UTC
    return {
      start: fromZonedTime(startOfToday, timezone),
      end: fromZonedTime(endOfToday, timezone),
    };
  }, [timezone]);

  /**
   * Get date range in UTC for API queries
   * Takes date strings and returns UTC range for that period in org timezone
   */
  const getDateRangeUTC = useCallback((
    startDate: string | Date,
    endDate?: string | Date
  ): { start: Date; end: Date } => {
    try {
      const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
      const end = endDate
        ? (typeof endDate === 'string' ? parseISO(endDate) : endDate)
        : start;

      // Start of first day and end of last day in org timezone
      const startZoned = startOfDay(start);
      const endZoned = endOfDay(end);

      // Convert to UTC
      return {
        start: fromZonedTime(startZoned, timezone),
        end: fromZonedTime(endZoned, timezone),
      };
    } catch {
      const now = new Date();
      return { start: now, end: now };
    }
  }, [timezone]);

  /**
   * Check if date is today in org timezone
   */
  const isToday = useCallback((date: string | Date): boolean => {
    const parsed = parseDate(date);
    if (!parsed) return false;

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      const zonedNow = toZonedTime(new Date(), timezone);

      return format(zonedDate, 'yyyy-MM-dd') === format(zonedNow, 'yyyy-MM-dd');
    } catch {
      return false;
    }
  }, [timezone]);

  /**
   * Check if date is in the past (before today in org timezone)
   */
  const isPast = useCallback((date: string | Date): boolean => {
    const parsed = parseDate(date);
    if (!parsed) return false;

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      const zonedNow = toZonedTime(new Date(), timezone);

      // Compare dates only (not time)
      const dateStr = format(zonedDate, 'yyyy-MM-dd');
      const nowStr = format(zonedNow, 'yyyy-MM-dd');

      return dateStr < nowStr;
    } catch {
      return false;
    }
  }, [timezone]);

  /**
   * Check if date is in the future (after today in org timezone)
   */
  const isFuture = useCallback((date: string | Date): boolean => {
    const parsed = parseDate(date);
    if (!parsed) return false;

    try {
      const zonedDate = toZonedTime(parsed, timezone);
      const zonedNow = toZonedTime(new Date(), timezone);

      // Compare dates only (not time)
      const dateStr = format(zonedDate, 'yyyy-MM-dd');
      const nowStr = format(zonedNow, 'yyyy-MM-dd');

      return dateStr > nowStr;
    } catch {
      return false;
    }
  }, [timezone]);

  // Context value
  const value: TimezoneContextType = useMemo(() => ({
    timezone,
    dateFormat,
    timeFormat,
    formatDate,
    formatTime,
    formatDateTime,
    formatDateShort,
    formatDateFull,
    formatDateTimeDisplay,
    formatRelativeTime,
    toUTC,
    getCurrentDate,
    getCurrentDateString,
    getDateNDaysAgo,
    getTodayRangeUTC,
    getDateRangeUTC,
    isToday,
    isPast,
    isFuture,
  }), [
    timezone,
    dateFormat,
    timeFormat,
    formatDate,
    formatTime,
    formatDateTime,
    formatDateShort,
    formatDateFull,
    formatDateTimeDisplay,
    formatRelativeTime,
    toUTC,
    getCurrentDate,
    getCurrentDateString,
    getDateNDaysAgo,
    getTodayRangeUTC,
    getDateRangeUTC,
    isToday,
    isPast,
    isFuture,
  ]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

/**
 * useTimezone Hook - Access timezone context
 * @throws Error if used outside TimezoneProvider
 */
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);

  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }

  return context;
};

// ============================================================================
// EXPORTS
// ============================================================================

export default TimezoneContext;
